// src/pages/UserMeet/MeetingInvitePage.jsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Send,
  X,
  Plus,
  Check,
  Calendar,
  Clock,
  Link2,
  Users,
  Copy,
  AlertCircle,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import {
  sendMeetingInvite,
  getMeetingDetails,
} from "@/Services/UserMeetServices";

// ---------------------------------------------------------------------------
// Email validation
// ---------------------------------------------------------------------------
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim().toLowerCase());

// ---------------------------------------------------------------------------
// Email Tag
// ---------------------------------------------------------------------------
function EmailTag({ email, onRemove, invalid }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
        invalid
          ? "bg-red-500/15 border-red-500/40 text-red-400"
          : "bg-[#C6A87C]/10 border-[#C6A87C]/30 text-[#C6A87C]"
      }`}
    >
      <Mail size={12} />
      <span className="max-w-[200px] truncate">{email}</span>
      <button
        onClick={() => onRemove(email)}
        className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
        type="button"
      >
        <X size={10} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result Row
// ---------------------------------------------------------------------------
function ResultRow({ result }) {
  const success = result.status === "sent";
  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
        success
          ? "bg-green-500/8 border-green-500/20"
          : "bg-red-500/8 border-red-500/20"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          success ? "bg-green-500/20" : "bg-red-500/20"
        }`}
      >
        {success ? (
          <Check size={14} className="text-green-400" />
        ) : (
          <X size={14} className="text-red-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {result.email}
        </p>
        <p
          className={`text-xs ${
            success ? "text-green-400/70" : "text-red-400/70"
          }`}
        >
          {success
            ? "Invite sent with calendar attachment"
            : result.error || "Failed to send"}
        </p>
      </div>
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
          success
            ? "bg-green-500/20 text-green-400"
            : "bg-red-500/20 text-red-400"
        }`}
      >
        {success ? "Sent" : "Failed"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function MeetingInvitePage() {
  // ✅ Use joinCode param — consistent with all other UserMeet routes
  const { joinCode } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  // ✅ state can pass meetingData directly to avoid an extra API call
  const [meeting, setMeeting] = useState(state?.meetingData || null);
  const [loadingMeeting, setLoadingMeeting] = useState(!state?.meetingData);

  const [emails, setEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const inputRef = useRef(null);

  const meetingJoinCode = meeting?.joinCode || joinCode;
  const shareLink =
    meeting?.shareableLink ||
    (meetingJoinCode
      ? `${window.location.origin}/user-meet/join/${meetingJoinCode}`
      : "");

  /* ─── Fetch meeting by joinCode if not in state ─── */
  useEffect(() => {
    if (meeting || !joinCode) return;

    let cancelled = false;

    (async () => {
      try {
        setLoadingMeeting(true);

        // ✅ getMeetingDetails accepts joinCode directly
        const res = await getMeetingDetails(joinCode);

        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Meeting not found");

        setMeeting(res.data);
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.message || "Failed to load meeting");
        navigate("/user-meet", { replace: true });
      } finally {
        if (!cancelled) setLoadingMeeting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [joinCode, meeting, navigate]);

  /* ─── Page title ─── */
  useEffect(() => {
    document.title = meeting?.title
      ? `Invite to ${meeting.title} | UserMeet`
      : "Send Invite | UserMeet";
  }, [meeting?.title]);

  /* ─── Add email(s) from input ─── */
  const addEmails = useCallback(
    (raw) => {
      setInputError("");

      const parts = raw
        .split(/[\s,;]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);

      if (parts.length === 0) return;

      const newEmails = [];
      const invalid = [];

      parts.forEach((email) => {
        if (!isValidEmail(email)) {
          invalid.push(email);
        } else if (!emails.includes(email)) {
          newEmails.push(email);
        }
      });

      if (invalid.length > 0) {
        setInputError(
          `Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`,
        );
      }

      if (newEmails.length > 0) {
        setEmails((prev) => [...prev, ...newEmails]);
      }

      setInputValue("");
    },
    [emails],
  );

  /* ─── Handle input key events ─── */
  const handleKeyDown = useCallback(
    (e) => {
      if (["Enter", "Tab", ",", ";", " "].includes(e.key)) {
        e.preventDefault();
        if (inputValue.trim()) addEmails(inputValue);
      }
      if (e.key === "Backspace" && !inputValue && emails.length > 0) {
        setEmails((prev) => prev.slice(0, -1));
      }
    },
    [inputValue, emails.length, addEmails],
  );

  /* ─── Handle paste ─── */
  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text");
      addEmails(pasted);
    },
    [addEmails],
  );

  /* ─── Remove single email ─── */
  const removeEmail = useCallback((email) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  }, []);

  /* ─── Clear all ─── */
  const clearAll = useCallback(() => {
    setEmails([]);
    setInputValue("");
    setInputError("");
  }, []);

  /* ─── Copy helpers ─── */
  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(shareLink);
    setCopiedLink(true);
    toast.success("Share link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  }, [shareLink]);

  const copyCode = useCallback(() => {
    if (!meetingJoinCode) return;
    navigator.clipboard.writeText(meetingJoinCode);
    setCopiedCode(true);
    toast.success("Join code copied!");
    setTimeout(() => setCopiedCode(false), 2000);
  }, [meetingJoinCode]);

  /* ─── Send invites ─── */
  const handleSend = useCallback(async () => {
    // Flush any un-committed input first
    if (inputValue.trim()) {
      addEmails(inputValue);
      // Let React re-render with the new email before continuing
      return;
    }

    if (emails.length === 0) {
      setInputError("Please add at least one email address.");
      inputRef.current?.focus();
      return;
    }

    if (!meeting?.meetingId) {
      toast.error(
        "Meeting information is missing. Please go back and try again.",
      );
      return;
    }

    try {
      setSending(true);

      const res = await sendMeetingInvite(meeting.meetingId, emails);

      if (!res?.success) {
        throw new Error(res?.message || "Failed to send invites.");
      }

      setResults(res.data?.results || []);
      toast.success(
        res.message ||
          `Invites sent to ${emails.length} recipient${emails.length !== 1 ? "s" : ""}!`,
      );
    } catch (err) {
      toast.error(err?.message || "Failed to send invites. Please try again.");
    } finally {
      setSending(false);
    }
  }, [emails, inputValue, meeting, addEmails]);

  /* ─── Reset to compose view ─── */
  const handleSendMore = useCallback(() => {
    setResults(null);
    setEmails([]);
    setInputValue("");
    setInputError("");
  }, []);

  // ---------------------------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------------------------
  if (loadingMeeting) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/40">Loading meeting details…</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* ── Back ── */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-8 inline-flex items-center gap-2 text-xs text-white/55 hover:text-white/90 transition-colors group"
        >
          <ArrowLeft
            size={14}
            className="group-hover:-translate-x-0.5 transition-transform"
          />
          Back
        </button>

        {/* ── Hero ── */}
        <div className="mb-10">
          <p className="text-[10px] font-semibold tracking-widest uppercase text-[#C6A87C]/70 mb-3">
            Meeting Invite
          </p>
          <h1
            className="font-light text-4xl md:text-5xl tracking-tighter leading-none mb-3 text-white"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Invite people to{" "}
            <span className="italic text-[#C6A87C]">
              {meeting?.title || "your meeting"}
            </span>
          </h1>
          <p className="text-white/50 text-sm">
            Recipients will receive a premium email with a calendar attachment
            (.ics) they can add directly to Google Calendar, Outlook, or Apple
            Calendar.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ── LEFT: Email compose area ── */}
          <div className="lg:col-span-2 space-y-6">
            {/* ── Success / Results State ── */}
            {results ? (
              <div className="glass rounded-2xl border border-white/[0.06] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 border-b border-white/10 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                    <CheckCircle2 size={20} className="text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Invites Sent</p>
                    <p className="text-sm text-white/50">
                      {results.filter((r) => r.status === "sent").length}{" "}
                      succeeded
                      {results.filter((r) => r.status !== "sent").length > 0 &&
                        `, ${results.filter((r) => r.status !== "sent").length} failed`}
                    </p>
                  </div>
                </div>

                {/* Results list */}
                <div className="px-6 py-5 space-y-3 max-h-80 overflow-y-auto">
                  {results.map((result) => (
                    <ResultRow key={result.email} result={result} />
                  ))}
                </div>

                {/* Actions */}
                <div className="px-6 py-5 border-t border-white/10 flex items-center gap-3">
                  <button
                    onClick={handleSendMore}
                    type="button"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C6A87C]/10 border border-[#C6A87C]/30 text-[#C6A87C] text-sm font-semibold hover:bg-[#C6A87C]/20 hover:border-[#C6A87C]/50 transition-all"
                  >
                    <Plus size={16} />
                    Invite More
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    type="button"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white/70 text-sm font-semibold hover:bg-white/10 hover:border-white/25 transition-all"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              /* ── Compose State ── */
              <div className="glass rounded-2xl border border-white/[0.06] overflow-hidden">
                {/* To field */}
                <div className="px-6 py-5 border-b border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">
                      To
                    </p>
                    {emails.length > 0 && (
                      <button
                        onClick={clearAll}
                        type="button"
                        className="text-[10px] text-white/30 hover:text-red-400 transition-colors"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Tags + input */}
                  <div
                    className="min-h-[100px] flex flex-wrap gap-2 items-start pt-1 cursor-text"
                    onClick={() => inputRef.current?.focus()}
                  >
                    {emails.map((email) => (
                      <EmailTag
                        key={email}
                        email={email}
                        onRemove={removeEmail}
                        invalid={!isValidEmail(email)}
                      />
                    ))}

                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        setInputError("");
                      }}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      onBlur={() => {
                        if (inputValue.trim()) addEmails(inputValue);
                      }}
                      placeholder={
                        emails.length === 0
                          ? "Enter email addresses (press Enter, comma, or space to add)"
                          : "Add more..."
                      }
                      className="flex-1 min-w-[200px] bg-transparent text-sm text-white placeholder:text-white/25 focus:outline-none"
                      disabled={sending}
                    />
                  </div>

                  {/* Validation error */}
                  {inputError && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-red-400">
                      <AlertCircle size={12} />
                      <span>{inputError}</span>
                    </div>
                  )}
                </div>

                {/* Recipient count banner */}
                {emails.length > 0 && (
                  <div className="px-6 py-3 bg-[#C6A87C]/5 border-b border-white/5">
                    <p className="text-xs text-[#C6A87C]/70">
                      <span className="font-semibold text-[#C6A87C]">
                        {emails.length}
                      </span>{" "}
                      recipient{emails.length !== 1 ? "s" : ""} will receive an
                      invite with a calendar attachment
                    </p>
                  </div>
                )}

                {/* What they'll receive */}
                <div className="px-6 py-5 border-b border-white/10">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40 mb-4">
                    What recipients will receive
                  </p>

                  <div className="space-y-3">
                    {[
                      {
                        icon: Mail,
                        label: "Premium HTML email",
                        desc: "Dark-themed branded invitation with meeting details",
                        color: "text-blue-400",
                        bg: "bg-blue-500/15 border-blue-500/25",
                      },
                      {
                        icon: Calendar,
                        label: "Calendar attachment (.ics)",
                        desc: "Auto-detected by Gmail, Outlook & Apple Mail as a calendar invite",
                        color: "text-green-400",
                        bg: "bg-green-500/15 border-green-500/25",
                      },
                      {
                        icon: Clock,
                        label: "15-minute reminder",
                        desc: "Pre-configured reminder built into the calendar event",
                        color: "text-yellow-400",
                        bg: "bg-yellow-500/15 border-yellow-500/25",
                      },
                      {
                        icon: Link2,
                        label: "Direct join link",
                        desc: "One-click link to join the meeting from the email",
                        color: "text-purple-400",
                        bg: "bg-purple-500/15 border-purple-500/25",
                      },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.label}
                          className={`flex items-start gap-3 p-3 rounded-xl border ${item.bg}`}
                        >
                          <div
                            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${item.bg}`}
                          >
                            <Icon size={14} className={item.color} />
                          </div>
                          <div>
                            <p
                              className={`text-sm font-semibold ${item.color}`}
                            >
                              {item.label}
                            </p>
                            <p className="text-xs text-white/50 mt-0.5">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Tip */}
                <div className="px-6 py-4 bg-white/[0.02]">
                  <div className="flex items-start gap-2 text-xs text-white/40">
                    <Sparkles
                      size={12}
                      className="text-[#C6A87C]/60 mt-0.5 flex-shrink-0"
                    />
                    <p>
                      Tip: You can paste multiple email addresses at once, or
                      separate them with commas, semicolons, or line breaks.
                    </p>
                  </div>
                </div>

                {/* Send button */}
                <div className="px-6 py-5 border-t border-white/10">
                  <button
                    onClick={handleSend}
                    disabled={
                      sending || (emails.length === 0 && !inputValue.trim())
                    }
                    type="button"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-[#C6A87C] text-[#061412] font-semibold text-sm hover:bg-[#D9BC93] transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
                  >
                    {sending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-[#061412]/30 border-t-[#061412] rounded-full animate-spin" />
                        Sending invites…
                      </>
                    ) : (
                      <>
                        <Send
                          size={18}
                          className="group-hover:translate-x-0.5 transition-transform"
                        />
                        Send{" "}
                        {emails.length > 0
                          ? `${emails.length} Invite${emails.length !== 1 ? "s" : ""}`
                          : "Invites"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: Meeting info sidebar ── */}
          <div className="space-y-5">
            {/* Meeting details */}
            <div className="glass rounded-2xl border border-white/[0.06] p-5 space-y-5">
              <div>
                <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40 mb-3">
                  Meeting Details
                </p>
                <h3
                  className="text-lg font-light tracking-tight text-white mb-1 line-clamp-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {meeting?.title || "Untitled Meeting"}
                </h3>
                {meeting?.description && (
                  <p className="text-xs text-white/45 line-clamp-3">
                    {meeting.description}
                  </p>
                )}
              </div>

              <div className="space-y-2.5">
                <div className="flex items-center gap-3 text-xs">
                  <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Users size={12} className="text-[#C6A87C]" />
                  </div>
                  <div>
                    <p className="text-white/40">Capacity</p>
                    <p className="text-white/80 font-medium">
                      {meeting?.currentOccupancy || 0} /{" "}
                      {meeting?.capacity || 8} participants
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs">
                  <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Calendar size={12} className="text-[#C6A87C]" />
                  </div>
                  <div>
                    <p className="text-white/40">
                      {meeting?.scheduledAt ? "Scheduled" : "Created"}
                    </p>
                    <p className="text-white/80 font-medium">
                      {meeting?.scheduledAt
                        ? new Date(meeting.scheduledAt).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )
                        : new Date(
                            meeting?.createdAt || Date.now(),
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                    </p>
                  </div>
                </div>

                {meeting?.scheduledEndAt && (
                  <div className="flex items-center gap-3 text-xs">
                    <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                      <Clock size={12} className="text-[#C6A87C]" />
                    </div>
                    <div>
                      <p className="text-white/40">Ends at</p>
                      <p className="text-white/80 font-medium">
                        {new Date(meeting.scheduledEndAt).toLocaleTimeString(
                          "en-US",
                          { hour: "2-digit", minute: "2-digit" },
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick share card */}
            <div className="glass rounded-2xl border border-white/[0.06] p-5 space-y-4">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">
                Quick Share
              </p>

              {/* Join Code */}
              <div>
                <p className="text-xs text-white/40 mb-2">Join Code</p>
                <div className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <span className="flex-1 font-mono text-[#C6A87C] text-sm tracking-widest">
                    {meetingJoinCode || "—"}
                  </span>
                  <button
                    onClick={copyCode}
                    type="button"
                    className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#C6A87C]/10 hover:border-[#C6A87C]/30 transition-all"
                  >
                    {copiedCode ? (
                      <Check size={14} className="text-green-400" />
                    ) : (
                      <Copy size={14} className="text-white/50" />
                    )}
                  </button>
                </div>
              </div>

              {/* Share Link */}
              <div>
                <p className="text-xs text-white/40 mb-2">Share Link</p>
                <div className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <span className="flex-1 font-mono text-white/50 text-xs truncate">
                    {shareLink}
                  </span>
                  <button
                    onClick={copyLink}
                    type="button"
                    className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#C6A87C]/10 hover:border-[#C6A87C]/30 transition-all flex-shrink-0"
                  >
                    {copiedLink ? (
                      <Check size={14} className="text-green-400" />
                    ) : (
                      <Copy size={14} className="text-white/50" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Calendar note */}
            <div className="rounded-2xl border border-[#C6A87C]/15 bg-[#C6A87C]/5 p-5">
              <div className="flex gap-3">
                <Calendar
                  size={16}
                  className="text-[#C6A87C] flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-sm font-semibold text-[#C6A87C] mb-1">
                    Google Calendar Integration
                  </p>
                  <p className="text-xs text-white/50 leading-relaxed">
                    Gmail automatically detects the .ics attachment and shows a
                    prominent{" "}
                    <span className="text-white/70 font-medium">
                      "Add to Calendar"
                    </span>{" "}
                    banner at the top of the email. The event includes a
                    15-minute reminder.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
